import React, { useId, useMemo } from "react";
import "./MetricChartCard.css";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function coerceNumberList(values = []) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function toPoints(values, width, height, padding = 10) {
  if (!Array.isArray(values) || values.length === 0) return [];
  if (values.length === 1) {
    return [{ x: width / 2, y: height / 2 }];
  }

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const spread = Math.max(1, maxVal - minVal);
  const pad = spread * 0.18;
  const safeMin = minVal - pad;
  const safeMax = maxVal + pad;

  const usableWidth = width - (padding * 2);
  const usableHeight = height - (padding * 2);
  const stepX = usableWidth / (values.length - 1);

  return values.map((value, idx) => {
    const normalized = (value - safeMin) / (safeMax - safeMin);
    const x = padding + (stepX * idx);
    const y = padding + ((1 - clamp(normalized, 0, 1)) * usableHeight);
    return {
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    };
  });
}

function buildSmoothPath(points = []) {
  if (!Array.isArray(points) || points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const midX = ((current.x + next.x) / 2).toFixed(2);
    const midY = ((current.y + next.y) / 2).toFixed(2);
    d += ` Q ${current.x} ${current.y} ${midX} ${midY}`;
  }

  const last = points[points.length - 1];
  d += ` T ${last.x} ${last.y}`;
  return d;
}

function buildLinePaths(series = []) {
  const width = 340;
  const height = 138;
  const fallback = series.length === 1
    ? [series[0], series[0] * 0.98, series[0] * 1.02]
    : series;
  const points = toPoints(fallback, width, height, 10);
  if (!points.length) {
    return { width, height, linePath: "", areaPath: "" };
  }

  const linePath = buildSmoothPath(points);
  const first = points[0];
  const last = points[points.length - 1];
  const areaPath = `${linePath} L ${last.x} ${height} L ${first.x} ${height} Z`;
  return { width, height, linePath, areaPath };
}

export default function MetricChartCard({
  label,
  value,
  type = "line",
  series = [],
  percent = 0,
  tag = "",
  description = "",
}) {
  const gradientId = useId().replace(/:/g, "");

  const lineData = useMemo(
    () => buildLinePaths(coerceNumberList(series)),
    [series]
  );

  const radialSize = 124;
  const radialStroke = 12;
  const radialRadius = (radialSize / 2) - radialStroke;
  const radialDisplayValue =
    String(value || "").trim() || `${Math.round(clamp(Number(percent) || 0, 0, 1) * 100)}%`;

  return (
    <article className={`metric-chart-card metric-chart-card--${type}`}>
      <div className="metric-chart-card__head">
        <p className="metric-chart-card__title">
          <strong>{label}</strong>
        </p>
        {type === "line" ? (
          <>
            <p className="metric-chart-card__subtitle">{value}</p>
            {description ? (
              <p className="metric-chart-card__line-description">{description}</p>
            ) : null}
          </>
        ) : null}
        {tag ? <span className="metric-chart-card__tag">{tag}</span> : null}
      </div>

      <div
        className="metric-chart-card__visual"
        aria-hidden={type === "text" ? undefined : true}
      >
        {type === "text" ? (
          <div className="metric-chart-card__text-wrap">
            <p className="metric-chart-card__text-value">{value}</p>
            {description ? (
              <p className="metric-chart-card__text-description">{description}</p>
            ) : null}
          </div>
        ) : type === "radial" ? (
          <div className="metric-chart-card__radial-wrap">
            <svg
              className="metric-chart-card__radial-svg"
              viewBox={`0 0 ${radialSize} ${radialSize}`}
              role="img"
            >
              <circle
                className="metric-chart-card__radial-track"
                cx={radialSize / 2}
                cy={radialSize / 2}
                r={radialRadius}
              />
            </svg>
            <span className="metric-chart-card__radial-label">
              {radialDisplayValue}
            </span>
          </div>
        ) : (
          <svg
            className="metric-chart-card__line-svg"
            viewBox={`0 0 ${lineData.width} ${lineData.height}`}
            preserveAspectRatio="none"
            role="img"
          >
            <defs>
              <linearGradient id={`metric-line-${gradientId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
              <linearGradient id={`metric-area-${gradientId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(59, 130, 246, 0.36)" />
                <stop offset="100%" stopColor="rgba(59, 130, 246, 0.06)" />
              </linearGradient>
            </defs>
            {lineData.areaPath ? (
              <path
                className="metric-chart-card__area"
                d={lineData.areaPath}
                fill={`url(#metric-area-${gradientId})`}
              />
            ) : null}
            {lineData.linePath ? (
              <path
                className="metric-chart-card__line"
                d={lineData.linePath}
                fill="none"
                stroke={`url(#metric-line-${gradientId})`}
              />
            ) : null}
          </svg>
        )}
      </div>
    </article>
  );
}
